import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Phone, ChevronLeft, ChevronRight, MapPin, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BeforeAfterSlider({ beforeUrl, afterUrl }) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  };

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e) => { if (isDragging.current) handleMove(e.clientX); };
  const handleTouchMove = (e) => { handleMove(e.touches[0].clientX); };

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  });

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-xl cursor-col-resize select-none"
      onMouseDown={handleMouseDown}
      onTouchMove={handleTouchMove}
      data-testid="monument-slider"
    >
      <img src={afterUrl} alt="Etter" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
        <img src={beforeUrl} alt="For" className="absolute inset-0 w-full h-full object-cover" style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%', maxWidth: 'none' }} />
      </div>
      <div className="absolute top-0 bottom-0" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
        <div className="w-1 h-full bg-white shadow-lg" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
          <ChevronLeft size={14} className="text-stone-700 -mr-1" />
          <ChevronRight size={14} className="text-stone-700 -ml-1" />
        </div>
      </div>
      <div className="absolute bottom-3 left-3 bg-stone-900/80 text-white px-3 py-1 rounded-full text-xs font-semibold tracking-wide pointer-events-none">FØR</div>
      <div className="absolute bottom-3 right-3 bg-amber-700/80 text-white px-3 py-1 rounded-full text-xs font-semibold tracking-wide pointer-events-none">ETTER</div>
    </div>
  );
}

function MonumentPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/projects/public?project_type=monument`);
        if (res.ok) setProjects(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-testid="monument-back-btn">
            <ArrowLeft size={20} />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Gravsteinoppfriskeren
            </span>
          </button>
          <a href="tel:48147777" className="bg-stone-900 text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-stone-800 transition-colors hidden sm:inline-flex items-center gap-2" data-testid="monument-cta-phone">
            <Phone size={14} /> Ring oss
          </a>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-stone-900 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full mb-6 text-sm font-bold" data-testid="monument-badge">
            Profesjonell oppfrisking
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="monument-title">
            Monumentoppfrisking
          </h1>
          <p className="text-base md:text-lg text-stone-300 max-w-2xl leading-relaxed">
            Vi frisker opp monumenter, minnesmerker, statuer og andre steinarbeider over hele Norge. 
            Samme grundige prosess som for gravsteiner - med profesjonelt resultat.
          </p>
        </div>
      </div>

      {/* Services */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <h2 className="text-3xl sm:text-4xl font-semibold text-stone-900 mb-10" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="monument-services-title">
          Hva vi tilbyr
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 hover:shadow-lg transition-shadow">
            <h3 className="text-xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Grundig vask og rengjøring
            </h3>
            <p className="text-stone-600 leading-relaxed">
              Vi fjerner mose, sopp, alger og annen forurensning fra monumenter med skånsomme, profesjonelle metoder som bevarer steinens overflate og detaljer.
            </p>
          </div>
          <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 hover:shadow-lg transition-shadow">
            <h3 className="text-xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Oppfrisking av skrift og detaljer
            </h3>
            <p className="text-stone-600 leading-relaxed">
              Slitt skrift, årstall og dekorative elementer får nytt liv med to lag høykvalitets lakk. Vi kan også endre farge etter ønske.
            </p>
          </div>
          <div className="bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 hover:shadow-lg transition-shadow">
            <h3 className="text-xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Valgfri DC-Protect beskyttelse
            </h3>
            <p className="text-stone-600 leading-relaxed">
              For monumenter som ønskes beskyttet kan vi også påføre vår unike DC-Protect beskyttelse av flytende silisiumdioksid (SiO2), også kjent som flytende kvarts, med 10 års garanti.
            </p>
          </div>
        </div>
      </div>

      {/* Types of monuments */}
      <div className="bg-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-semibold text-stone-900 mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>
            Vi frisker opp alle typer monumenter
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              'Krigsminnesmerker',
              'Bauta og minnesteiner',
              'Statuer og skulpturer',
              'Kirkegårdsmonumenter',
              'Historiske monumenter',
              'Bedriftsmonumenter'
            ].map((type) => (
              <div key={type} className="flex items-center gap-3 bg-stone-50 rounded-xl p-4">
                <CheckCircle2 className="text-amber-600 flex-shrink-0" size={20} />
                <span className="text-sm sm:text-base font-medium text-stone-800">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <h2 className="text-3xl sm:text-4xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="monument-gallery-title">
          Før og etter
        </h2>
        <p className="text-stone-500 mb-10">Se resultater fra våre monumentoppfriskinger</p>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-200" data-testid="monument-gallery-empty">
            <p className="text-stone-500 text-lg mb-2">Galleri for monumenter kommer snart!</p>
            <p className="text-stone-400">Vi legger fortløpende ut bilder fra våre prosjekter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="monument-gallery-grid">
            {projects.map((project) => {
              const beforeImgs = (project.images || []).filter(i => i.category === 'before').sort((a, b) => a.position - b.position);
              const afterImgs = (project.images || []).filter(i => i.category === 'after').sort((a, b) => a.position - b.position);
              const mainBefore = beforeImgs[0]?.image_url;
              const mainAfter = afterImgs[0]?.image_url;
              if (!mainBefore || !mainAfter) return null;
              return (
                <div key={project.id} className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid={`monument-project-${project.id}`}>
                  <BeforeAfterSlider beforeUrl={mainBefore} afterUrl={mainAfter} />
                  <div className="p-5">
                    {project.gravestone_name && (
                    <h3 className="text-lg font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {project.gravestone_name}
                    </h3>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-500">
                      {(project.cemetery || project.municipality) && (
                        <span className="flex items-center gap-1"><MapPin size={14} /> {[project.cemetery, project.municipality].filter(Boolean).join(', ')}</span>
                      )}
                      {project.work_date && <span className="flex items-center gap-1"><Calendar size={14} /> {project.work_date}</span>}
                    </div>
                    {project.notes && <p className="text-sm text-stone-400 mt-2 italic">{project.notes}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-stone-900 text-white py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Har du et monument som trenger oppfrisking?
          </h2>
          <p className="text-stone-300 mb-8 leading-relaxed">
            Kontakt oss for et uforpliktende pristilbud. Vi utfører alt arbeid på stedet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:48147777" className="inline-flex items-center justify-center gap-2 bg-white text-stone-900 hover:bg-stone-100 rounded-full px-8 py-3.5 font-semibold transition-colors" data-testid="monument-cta-call">
              <Phone size={18} /> Ring 48 14 77 77
            </a>
            <button onClick={() => navigate('/#contact')} className="inline-flex items-center justify-center bg-amber-600 text-white hover:bg-amber-700 rounded-full px-8 py-3.5 font-semibold transition-colors" data-testid="monument-cta-form">
              Send forespørsel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonumentPage;
